/**
 * CEditableTable
 * @author Tevin
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Table, Tooltip } from 'antd';
import { LoadingOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { Tools } from '@components/common/Tools';
import { CTableCellInput } from './CTableCellInput';
import './cEditableTable.scss';

export class CEditableTable extends React.Component {
    static propTypes = {
        // 列配置
        columns: PropTypes.array,
        // 表格数据
        dataSource: PropTypes.array,
        // 表格翻页配置
        pagination: PropTypes.object,
        // 表格加载状态
        tableLoading: PropTypes.bool,
        // 滚动高度
        scrollHeight: PropTypes.number,
        // 是否开启自动最大高度
        autoMaxHeigh: PropTypes.bool,
        // 自动高度时，需要减去的额外高度
        trimHeight: PropTypes.number,
        // 表格翻页变化后的回调
        onChange: PropTypes.func,
        // 表格单项编辑后的回调
        onColumnChange: PropTypes.func,
    };

    static defaultProps = {
        scrollHeight: 400,
        autoMaxHeigh: true,
        trimHeight: 0,
    };

    constructor(props) {
        super(props);
        this.state = {
            scrollHeight: this.props.scrollHeight,
            // 划区显示状态
            drawing: false,
            // 划区选择方阵
            drawArray: [],
        };
        this.$refs = {};
        this._data = {
            drawMouseStart: false, // 开始划区
            drawStartId: [], // 开始id
            drawStartPos: [], // 鼠标开始位置
            tableDataArray: [],
            tableDataOffset: [], // 复制区域位置偏移量
        };
        this._listeners = {};
    }

    componentDidMount() {
        this._createColumns();
        // 尺寸缩放
        this._listeners.onResize = () => {
            if (!this.props.autoMaxHeigh) {
                return;
            }
            if (!this.$refs.container) {
                return;
            }
            // 按需填充，填充到满屏为止
            const containerTop = this.$refs.container.getBoundingClientRect().y;
            // 标题高度
            const headH = 48;
            // 页码高度
            const paginationH = this.props.pagination ? 57 : 0;
            // 底部距离，内容区内边距 + 窗口区内边距
            const endSideH = 15 + 10;
            // 填充高度
            const fillHeight =
                window.innerHeight -
                containerTop -
                headH -
                paginationH -
                endSideH -
                this.props.trimHeight;
            const minimumHeight = window.innerWidth >= 576 ? 300 : 355;
            this.setState({
                // 保留表格最小高度
                scrollHeight: Math.max(fillHeight, minimumHeight),
            });
        };
        setTimeout(this._listeners.onResize, 0);
        // 划区选择
        this._listeners.onMouseMove = evt => {
            this._handleSelectDrawMoving(evt);
        };
        this._listeners.onMouseUp = () => {
            this._handleSelectDrawEnd();
        };
        this._listeners.onClick = () => {
            this._handleSelectDrawClean();
        };
        this._listeners.onCopy = evt => {
            this._handleSelectDrawCopy(evt);
        };
        window.addEventListener('resize', this._listeners.onResize);
        document.addEventListener('mousemove', this._listeners.onMouseMove);
        document.addEventListener('mouseup', this._listeners.onMouseUp);
        document.addEventListener('click', this._listeners.onClick);
        document.addEventListener('copy', this._listeners.onCopy);
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this._listeners.onResize);
        document.removeEventListener('mousemove', this._listeners.onMouseMove);
        document.removeEventListener('mouseup', this._listeners.onMouseUp);
        document.removeEventListener('click', this._listeners.onClick);
        document.removeEventListener('copy', this._listeners.onCopy);
    }

    // 划区选择触发
    _handleSelectDrawStart(evt) {
        if (!evt.target) {
            return;
        }
        // 没有id跳过
        const dataArrayId = evt.target.getAttribute('data-array');
        if (!dataArrayId) {
            return;
        }
        this._data.drawMouseStart = true;
        this._data.drawStartId = dataArrayId.split('-').map(id => Number(id));
        this._data.drawStartPos = [evt.pageX, evt.pageY];
        // 每次开始重置已选
        this.setState({ drawing: false, drawArray: [] });
    }

    // 划区选择开始
    _handleSelectDrawMoving(evt) {
        if (!this._data.drawMouseStart) {
            return;
        }
        if (!evt.target) {
            return;
        }
        // 移动距离太小，跳过
        if (
            Math.abs(evt.pageX - this._data.drawStartPos[0]) < 20 &&
            Math.abs(evt.pageY - this._data.drawStartPos[1]) < 20
        ) {
            return;
        }
        // 没有id跳过
        const dataArrayId = evt.target.getAttribute('data-array');
        if (!dataArrayId) {
            return;
        }
        const [startColIndex, startRowIndex] = this._data.drawStartId;
        const [endColIndex, endRowIndex] = dataArrayId.split('-').map(id => Number(id));
        // 向右下方
        if (endColIndex >= startColIndex && endRowIndex >= startRowIndex) {
            this._calcDrawingCell(startColIndex, startRowIndex, endColIndex, endRowIndex);
        }
        // 向左上方
        else if (endColIndex <= startColIndex && endRowIndex <= startRowIndex) {
            this._calcDrawingCell(endColIndex, endRowIndex, startColIndex, startRowIndex);
        }
        // 向左下方
        else if (endColIndex <= startColIndex && endRowIndex >= startRowIndex) {
            this._calcDrawingCell(endColIndex, startRowIndex, startColIndex, endRowIndex);
        }
        // 向右上方
        else if (endColIndex >= startColIndex && endRowIndex <= startRowIndex) {
            this._calcDrawingCell(startColIndex, endRowIndex, endColIndex, startRowIndex);
        }
    }

    _calcDrawingCell(startColIndex, startRowIndex, endColIndex, endRowIndex) {
        const drawArray = [];
        // 对每个选中框进行选中判断，选中加底色
        for (let i = 0; i <= endColIndex; i++) {
            drawArray[i] = [];
            if (i >= startColIndex) {
                for (let j = 0; j <= endRowIndex; j++) {
                    // 选中的
                    if (j >= startRowIndex) {
                        drawArray[i][j] = 'drawing-inside';
                    }
                    // 未选中
                    else {
                        drawArray[i][j] = '';
                    }
                }
            } else {
                for (let k = 0; k <= endRowIndex; k++) {
                    // 未选中
                    drawArray[i][k] = '';
                }
            }
        }
        // 选中的框添加边线
        if (drawArray[startColIndex]) {
            drawArray[startColIndex][startRowIndex] += ' drawing-top drawing-left';
            drawArray[startColIndex][endRowIndex] += ' drawing-left drawing-bottom';
            if (endRowIndex - startRowIndex > 1) {
                for (let i = startRowIndex + 1; i < endRowIndex; i++) {
                    drawArray[startColIndex][i] += ' drawing-left';
                }
            }
        }
        if (drawArray[endColIndex]) {
            drawArray[endColIndex][startRowIndex] += ' drawing-top drawing-right';
            drawArray[endColIndex][endRowIndex] += ' drawing-right drawing-bottom';
            if (endRowIndex - startRowIndex > 1) {
                for (let i = startRowIndex + 1; i < endRowIndex; i++) {
                    drawArray[endColIndex][i] += ' drawing-right';
                }
            }
        }
        if (endColIndex - startColIndex > 1) {
            for (let i = startColIndex + 1; i < endColIndex; i++) {
                if (drawArray[i]) {
                    drawArray[i][startRowIndex] += ' drawing-top';
                    drawArray[i][endRowIndex] += ' drawing-bottom';
                }
            }
        }
        this._data.tableDataOffset = [startColIndex, startRowIndex];
        this.setState({
            drawing: true,
            drawArray,
        });
    }

    // 划区选择结束
    _handleSelectDrawEnd() {
        setTimeout(() => {
            this._data.drawMouseStart = false;
        }, 10);
    }

    _handleSelectDrawClean() {
        if (this._data.drawMouseStart) {
            return;
        }
        // 全局点击，取消划区状态
        this.setState({ drawing: false, drawArray: [] });
    }

    // 拼合内容并复制到剪切板
    _handleSelectDrawCopy(evt) {
        // 选框取消了，终止
        if (!this.state.drawing) {
            return;
        }
        if (this.state.drawArray.length === 0) {
            return;
        }
        const nextDrawArray = [];
        const [colOffset, rowOffset] = this._data.tableDataOffset;
        const copyArr = [];
        this.state.drawArray.forEach((col, colIndex) => {
            nextDrawArray.push(col);
            col.forEach((drawState, rowIndex) => {
                if (!drawState || drawState === '') {
                    return;
                }
                col[rowIndex] += ' drawing-copying';
                if (typeof copyArr[rowIndex - rowOffset] === 'undefined') {
                    copyArr[rowIndex - rowOffset] = [];
                }
                copyArr[rowIndex - rowOffset][colIndex - colOffset] =
                    this._data.tableDataArray[colIndex][rowIndex].value || '';
            });
        });
        const copyData = copyArr.map(row => row.join('\t')).join('\n');
        evt.clipboardData.setData('text/plain', copyData);
        evt.preventDefault();
        this.setState({ drawArray: nextDrawArray });
    }

    // 粘贴区域
    _handlePasteArea(evt) {
        const { start, content } = evt;
        const pasteArray = content
            .replace(/[\r\n]*$/, '')
            .split(/[\r\n]+/)
            .map(row => row.split(/\t/));
        const rectangle = [
            start[0],
            start[1],
            start[0] + pasteArray[0].length - 1,
            start[1] + pasteArray.length - 1,
        ];
        this._calcDrawingCell(...rectangle);
        // 输入内容
        for (let i = 0; i <= rectangle[2]; i++) {
            if (i >= rectangle[0]) {
                for (let j = 0; j <= rectangle[3]; j++) {
                    // 选中的
                    if (j >= rectangle[1]) {
                        const datas = this._data.tableDataArray[i][j];
                        const pasteVal = pasteArray[j - start[1]][i - start[0]];
                        if (!Tools.isEmptyObject(datas)) {
                            this.props.onColumnChange({
                                ...datas,
                                value: pasteVal || '0',
                            });
                        }
                    }
                }
            }
        }
    }

    _handleColumnChange(index) {
        return evt => this.props.onColumnChange(evt);
    }

    _updateDataArray(colIndex, rowIndex, datas) {
        if (typeof this._data.tableDataArray[colIndex] === 'undefined') {
            this._data.tableDataArray[colIndex] = [];
        }
        this._data.tableDataArray[colIndex][rowIndex] = datas || {};
    }

    // 重设单列的列配置
    _createColumnBySingle(column, colIndex) {
        // 列标题宽度 = 转换后的字符个数 * 7 + 单元格内边距 + 排序图标宽度
        let headerWidth = column.title.replace(/[\u4e00-\u9fa5]/g, 'aa').length * 7 + 16;
        // 重设列配置
        const column2 = {
            width: Math.max(100, headerWidth),
            align: 'center', // 默认居中
            fixed: false, // 默认不开启固定
            sorter: false, // 默认关闭排序
            ...column,
            title: this._renderColTitleHelper(column),
            key: 'col-' + colIndex,
            onCell: (record, rowIndex) => {
                this._updateDataArray(colIndex, rowIndex, record[column.dataIndex]);
                const colDrawState = this.state.drawArray[colIndex] || [];
                return {
                    colIndex,
                    rowIndex,
                    drawing: this.state.drawing,
                    drawState: (colDrawState && colDrawState[rowIndex]) || '',
                    editable: column.editable,
                    width: column.width,
                    fixed: column.fixed,
                    datas: record[column.dataIndex],
                    value: record[column.dataIndex]
                        ? record[column.dataIndex].value
                        : null,
                    editSuffix: column.editSuffix,
                    onColumnChange: this._handleColumnChange(),
                    onSelectDrawStart: evt => this._handleSelectDrawStart(evt),
                    onPasteArea: evt => this._handlePasteArea(evt),
                };
            },
        };
        return column2;
    }

    // 重设带子级的列配置
    _createColumnByChildren(column, index) {
        // 父级列标题宽度 = 转换后的字符个数 * 7 + 单元格内边距
        const parentWidth =
            column.title.replace(/[\u4e00-\u9fa5]/g, 'aa').length * 7 + 16;
        // 子级列标题总宽度
        let childrenWidth = 0;
        const children = column.children
            .map((child, index2) => {
                // 子级标题宽度 = 转换后的字符个数 * 7 + 单元格内边距
                let childWidth =
                    child.title.replace(/[\u4e00-\u9fa5]/g, 'aa').length * 7 + 20;
                const child2 = {
                    width: Math.max(child.width || 0, childWidth),
                    align: 'center', // 默认居中
                    sorter: false, // 默认关闭排序
                    ...child,
                    fixed: false, // 子级强制不允许固定
                    title: this._renderColTitleHelper(child),
                    key: 'col-' + index + 'child-' + index2,
                    onCell: (record, rowIndex) => {
                        const colIndex = index + index2;
                        this._updateDataArray(
                            colIndex,
                            rowIndex,
                            record[child.dataIndex],
                        );
                        const colDrawState = this.state.drawArray[colIndex] || [];
                        return {
                            colIndex,
                            rowIndex,
                            drawing: this.state.drawing,
                            drawState: (colDrawState && colDrawState[rowIndex]) || '',
                            editable: child.editable,
                            width: child.width,
                            fixed: child.fixed,
                            datas: record[child.dataIndex],
                            value: record[child.dataIndex]
                                ? record[child.dataIndex].value
                                : null,
                            editSuffix: child.editSuffix,
                            onColumnChange: this._handleColumnChange(),
                            onSelectDrawStart: evt => this._handleSelectDrawStart(evt),
                            onPasteArea: evt => this._handlePasteArea(evt),
                        };
                    },
                };
                // 子级宽度累加
                childrenWidth += child2.width;
                return child2;
            })
            .filter(Boolean);
        // 重设列配置
        const column2 = {
            width: Math.max(parentWidth, childrenWidth),
            align: 'center', // 默认居中
            fixed: false, // 默认不开启固定
            ...column,
            sorter: false, // 父级强制不允许排序
            title: this._renderColTitleHelper(column),
            key: 'col-' + index,
            children,
        };
        return column2;
    }

    _createColumns() {
        let tableWidth = 0;
        let colIndex = 0;
        this._data.tableDataArray = [];
        const columns = this.props.columns.map((column, index) => {
            let column2 = null;
            // 普通列（没有子级）
            if (!column.children || column.children.length === 0) {
                column2 = this._createColumnBySingle(column, colIndex);
                colIndex++;
            }
            // 存在子级的列
            else {
                column2 = this._createColumnByChildren(column, colIndex);
                colIndex += column.children.length;
            }
            // 表格总宽，用于水平滚动
            tableWidth += column2 ? column2.width : 0;
            return column2;
        });
        return {
            columns,
            tableWidth,
        };
    }

    // 显示帮助信息
    _renderColTitleHelper(col) {
        if (!col.helper) {
            return col.title;
        } else {
            return (
                <span className="c-table-title-helper">
                    <Tooltip placement="top" title={col.helper}>
                        {col.title}
                        <QuestionCircleOutlined />
                    </Tooltip>
                </span>
            );
        }
    }

    render() {
        const { defaultColumns, autoMaxHeigh, ...resetProps } = this.props;
        const { columns, tableWidth } = this._createColumns();
        const components = {
            body: { cell: CTableCellInput },
        };
        const tableLoading = {
            delay: 100,
            indicator: <LoadingOutlined spin />,
            spinning: this.props.tableLoading,
        };
        const tableScroll = {
            x: this.state.tableWidth, // 表格计算宽度
            y: this.state.scrollHeight, // 最大填充至满屏
        };
        return (
            <div className="c-editable-table" ref={elm => (this.$refs.container = elm)}>
                <Table
                    {...resetProps}
                    columns={columns}
                    tableWidth={tableWidth}
                    rowKey={row => row.id || row.key}
                    size="middle"
                    bordered
                    components={components}
                    loading={tableLoading}
                    pagination={false}
                    scroll={tableScroll}
                />
            </div>
        );
    }
}
